import React from "react";

const appData = [
  {
    id: "cognitive",
    label: "Cognitive",
    note: "Thinking patterns, beliefs, rumination, guilt, and overthinking.",
    items: [
      {
        id: "neg-thinking",
        symptom: "Negative thinking, catastrophising",
        diagnosis: "Depression, Anxiety",
        example:
          "Persistent thoughts like \"I am a failure\", expecting the worst outcome, difficulty seeing positives, and interpreting neutral situations as signs that something bad will happen.",
        therapies: [
          { label: "CBT", brief: "Helps identify and reframe unhelpful thoughts into more balanced thinking.", expected: "Reduced negative thinking, improved mood, better coping." },
          { label: "ACT", brief: "Helps accept distressing thoughts without letting them control behaviour.", expected: "More values-based action despite anxious or depressive thoughts." },
          { label: "MBCT", brief: "Uses mindfulness to notice thoughts without reacting automatically.", expected: "Less rumination and less emotional reactivity to thoughts." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — first-line for depression and anxiety." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — useful where both depression and anxiety are prominent." },
        ],
        referrals: [
          { label: "Psychologist", brief: "General psychological therapy support for thought patterns and coping." },
          { label: "Clinical Psychologist", brief: "More specialised therapy for persistent or complex cognitive symptoms." },
        ],
      },
      {
        id: "rumination",
        symptom: "Rumination, relapse tendency",
        diagnosis: "Depression",
        example:
          "Repeatedly replaying past events, lying awake reviewing mistakes, getting stuck in negative thinking loops, and struggling to mentally switch off.",
        therapies: [
          { label: "MBCT", brief: "Teaches stepping back from repetitive negative thought loops.", expected: "Reduced rumination and reduced relapse risk." },
          { label: "CBT", brief: "Identifies the thinking patterns that keep low mood going.", expected: "Better control over negative thought spirals." },
          { label: "Metacognitive Therapy", brief: "Targets the process of overthinking rather than the content alone.", expected: "Less time spent overthinking and improved mental flexibility." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — reduces depressive symptom burden including rumination." },
          { label: "SNRI", brief: "Duloxetine 30mg/cap OD — particularly useful where low energy and mood overlap." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Structured therapy for rumination and mood relapse prevention." },
          { label: "Clinical Psychologist", brief: "Useful where depressive relapse is recurrent or complex." },
        ],
      },
      {
        id: "worry",
        symptom: "Excessive worry, overthinking",
        diagnosis: "Anxiety",
        example:
          "Constant \"what if\" thoughts about health, money, safety, family, or work; indecisiveness; mental exhaustion from over-analysing everyday issues.",
        therapies: [
          { label: "Metacognitive Therapy", brief: "Focuses on reducing worry loops and mental checking.", expected: "Less excessive worry and less mental exhaustion." },
          { label: "CBT", brief: "Challenges anxious predictions and thinking traps.", expected: "Improved confidence and reduced worry-driven avoidance." },
          { label: "ACT", brief: "Builds willingness to move forward even while anxious thoughts are present.", expected: "Better functioning despite ongoing anxious thoughts." },
        ],
        medications: [
          { label: "SSRI", brief: "Escitalopram 10mg/tab OD or Sertraline 50mg/tab OD — first-line for generalised anxiety disorder." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — evidence-based for GAD and mixed anxiety-depression." },
          { label: "Buspirone", brief: "5mg/tab BD (non-sedating anxiolytic) — adjunct for generalised anxiety; not PRN." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Therapy for worry, anxiety management, and cognitive coping." },
          { label: "Clinical Psychologist", brief: "Useful for more entrenched or disabling anxiety presentations." },
        ],
      },
      {
        id: "guilt-beliefs",
        symptom: "Guilt, distorted beliefs",
        diagnosis: "PTSD, Depression",
        example:
          "Feeling responsible for past events that were outside the person's control, excessive shame, self-blame, and difficulty accepting a more realistic interpretation.",
        therapies: [
          { label: "Cognitive Processing Therapy", brief: "Works through guilt and trauma-related beliefs in a structured way.", expected: "Reduced self-blame and more balanced trauma beliefs." },
          { label: "CBT", brief: "Challenges guilt-based thinking and distorted assumptions.", expected: "Improved self-view and less shame-driven distress." },
          { label: "EMDR", brief: "Processes distressing memories that keep guilt or shame active.", expected: "Reduced emotional intensity linked to past events." },
        ],
        medications: [
          { label: "SSRI", brief: "Sertraline 50mg/tab OD or Paroxetine 20mg/tab OD — evidence base for PTSD and comorbid depression." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — useful for PTSD with prominent mood and anxiety symptoms." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Specialised trauma-informed therapy for guilt and trauma beliefs." },
          { label: "Trauma Therapist", brief: "Focused treatment where traumatic memory processing is central." },
        ],
      },
    ],
  },
  {
    id: "behavioural",
    label: "Behaviour",
    note: "Avoidance, inactivity, compulsions, repetitive habits, and action-focused difficulties.",
    items: [
      {
        id: "low-motivation",
        symptom: "Low motivation, inactivity",
        diagnosis: "Depression",
        example:
          "Staying in bed for long periods, withdrawing from enjoyable activities, reduced self-care, reduced cooking or cleaning, and difficulty starting simple daily tasks.",
        therapies: [
          { label: "Behavioural Activation", brief: "Uses small, structured activities to rebuild routine and momentum.", expected: "Increased activity, more routine, and improved mood." },
          { label: "CBT", brief: "Targets the thoughts that keep motivation blocked.", expected: "Better engagement with everyday tasks and goals." },
          { label: "Problem-Solving Therapy", brief: "Breaks overwhelming tasks into achievable steps.", expected: "Improved task completion and reduced paralysis." },
          { label: "Community support programs", brief: "Adds practical structure, social contact, and regular engagement.", expected: "Better daily structure and reduced withdrawal." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — reduces depressive symptom burden including low motivation." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — activating profile may assist with energy and motivation." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Therapy for activation, planning, and mood-related inactivity." },
          { label: "Social Worker", brief: "Helps with community supports, practical barriers, and engagement." },
        ],
      },
      {
        id: "avoidance-fear",
        symptom: "Avoidance, fear response",
        diagnosis: "Anxiety, Phobia",
        example:
          "Avoiding roads, shopping centres, phone calls, public places, appointments, or social settings because they trigger panic, fear, or overwhelming discomfort.",
        therapies: [
          { label: "Exposure Therapy", brief: "Gradual, planned exposure reduces fear over time.", expected: "Reduced avoidance and increased confidence facing feared situations." },
          { label: "CBT", brief: "Challenges the beliefs that maintain fear and avoidance.", expected: "Improved ability to test fears more realistically." },
          { label: "ACT", brief: "Supports moving toward life goals even with anxiety present.", expected: "Greater participation in avoided activities." },
        ],
        medications: [
          { label: "SSRI", brief: "Escitalopram 10mg/tab OD or Sertraline 50mg/tab OD — first-line for anxiety and panic disorder." },
          { label: "Beta-blocker (PRN)", brief: "Propranolol 10–40mg/tab PRN — for situational/performance anxiety; not a daily medication." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Evidence-based treatment for avoidance and fear symptoms." },
          { label: "Clinical Psychologist", brief: "Specialist input where phobia or panic is severe or entrenched." },
        ],
      },
      {
        id: "ocd-compulsions",
        symptom: "Compulsions, intrusive thoughts",
        diagnosis: "OCD",
        example:
          "Repeated checking, washing, reassurance-seeking, or ritual behaviours driven by distressing intrusive thoughts and only temporary relief after completing the ritual.",
        therapies: [
          { label: "ERP", brief: "Helps face triggers without performing the compulsion.", expected: "Reduced compulsions and reduced distress linked to intrusive thoughts." },
          { label: "CBT", brief: "Targets the meaning given to obsessions and the urge to neutralise them.", expected: "Improved control over OCD patterns." },
        ],
        medications: [
          { label: "High-dose SSRI", brief: "Fluoxetine 40–80mg/cap OD or Sertraline 100–200mg/tab OD — higher doses required for OCD than depression." },
          { label: "TCA (specialist)", brief: "Clomipramine 25mg/tab OD (titrate to 150–250mg) — effective OCD medication; requires specialist oversight." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Best suited for specialised OCD treatment, especially ERP." },
        ],
      },
      {
        id: "repetitive-habits",
        symptom: "Repetitive habits, tics",
        diagnosis: "OCD spectrum, Tic disorders",
        example:
          "Nail biting, skin picking, hair pulling, blinking, or repetitive motor habits that occur automatically and are difficult to interrupt.",
        therapies: [
          { label: "Habit Reversal Therapy", brief: "Builds awareness and substitutes a competing response.", expected: "Reduced frequency and intensity of repetitive habits." },
          { label: "CBT", brief: "Improves awareness of triggers and coping alternatives.", expected: "Better control of habit patterns and less distress." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — for OCD-spectrum habits and body-focused repetitive behaviours." },
          { label: "Antipsychotic (tics)", brief: "Risperidone 0.5mg/tab OD — for tic disorders; review with psychiatrist." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Behavioural management for repetitive habits or tic-like symptoms." },
        ],
      },
      {
        id: "stress-eating",
        symptom: "Stress eating, emotional eating",
        diagnosis: "Stress-related eating difficulties, Depression, Anxiety",
        example:
          "Eating in response to stress, boredom, sadness, or overwhelm; grazing late at night; loss of control around comfort foods; guilt after eating.",
        therapies: [
          { label: "CBT", brief: "Identifies the thoughts, emotions, and triggers behind stress eating.", expected: "Improved eating awareness and fewer stress-driven eating episodes." },
          { label: "ACT", brief: "Builds distress tolerance without automatically using food to cope.", expected: "More values-based eating choices and less emotional eating." },
          { label: "Mindful eating strategies", brief: "Improves awareness of hunger, fullness, and emotional triggers.", expected: "Better recognition of triggers and more intentional eating." },
          { label: "Dietitian support", brief: "Provides nutrition structure and practical eating strategies.", expected: "More regular eating patterns and improved nutritional stability." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD — also approved for bulimia nervosa; reduces emotional eating urges." },
          { label: "SSRI (alternative)", brief: "Sertraline 50mg/tab OD — for comorbid depression or anxiety driving stress eating." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for emotional eating and coping-based food use." },
          { label: "Dietitian", brief: "Nutrition planning and behavioural eating support." },
        ],
      },
    ],
  },
  {
    id: "emotional",
    label: "Emotional",
    note: "Emotional dysregulation, shame, overwhelm, and distress tolerance difficulties.",
    items: [
      {
        id: "dysregulation",
        symptom: "Emotional dysregulation, impulsivity",
        diagnosis: "BPD, Trauma-related presentations",
        example:
          "Rapid mood changes, intense anger, urges to self-harm, impulsive spending, substance use when distressed, and difficulty settling after conflict or rejection.",
        therapies: [
          { label: "DBT", brief: "Teaches emotion regulation, distress tolerance, and safer coping.", expected: "Reduced impulsivity, better emotional control, reduced self-harm risk." },
          { label: "MBT", brief: "Builds capacity to understand one's own feelings and others' intentions.", expected: "Improved relationship stability and reduced emotional escalation." },
          { label: "Schema Therapy", brief: "Targets long-standing emotional patterns and triggers.", expected: "More stable coping and fewer repetitive emotional crises." },
        ],
        medications: [
          { label: "Mood stabiliser", brief: "Lamotrigine 25mg/tab OD (titrate slowly to 100–200mg) — reduces mood instability in BPD; psychiatrist oversight required." },
          { label: "Atypical antipsychotic", brief: "Quetiapine 25–50mg/tab nocte PRN — for acute distress or sleep disruption; review with psychiatrist." },
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD — for comorbid depression or anxiety component." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Best placed for structured therapy in complex emotional dysregulation." },
          { label: "Psychiatrist", brief: "Consider where risk, diagnostic complexity, or medication review is needed." },
        ],
      },
      {
        id: "low-self-esteem",
        symptom: "Low self-esteem, shame",
        diagnosis: "Depression, Trauma",
        example:
          "Harsh self-talk, chronic feelings of not being good enough, avoiding opportunities out of embarrassment, and comparing self negatively to others.",
        therapies: [
          { label: "Compassion-Focused Therapy", brief: "Builds self-kindness and reduces shame-based self-attack.", expected: "Reduced shame and improved self-worth." },
          { label: "CBT", brief: "Challenges negative beliefs about the self and personal worth.", expected: "More balanced self-appraisal and improved confidence." },
          { label: "Person-Centred Therapy", brief: "Offers a non-judgmental, supportive space to build self-acceptance.", expected: "Feeling more understood and less self-critical." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — addresses underlying depression driving low self-esteem." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Therapy support for shame, self-worth, and identity-related distress." },
        ],
      },
      {
        id: "poor-coping",
        symptom: "Poor coping, overwhelmed",
        diagnosis: "Depression, Adjustment difficulties",
        example:
          "Feeling stuck, putting off important tasks, shutting down under stress, and not knowing how to start when faced with multiple pressures.",
        therapies: [
          { label: "Problem-Solving Therapy", brief: "Turns large problems into smaller, practical steps.", expected: "Improved decision-making and better day-to-day coping." },
          { label: "CBT", brief: "Targets unhelpful beliefs that fuel helplessness or overwhelm.", expected: "Improved confidence in managing stressors." },
          { label: "Case management", brief: "Coordinates support where multiple problems need practical follow-through.", expected: "Better organisation of supports and reduced overwhelm." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — reduces depressive and anxiety load underlying poor coping." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — for mixed depression-anxiety presentations." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Psychological strategies for overwhelm and problem coping." },
          { label: "Social Worker", brief: "Practical assistance with complex psychosocial stressors." },
        ],
      },
    ],
  },
  {
    id: "trauma",
    label: "Trauma",
    note: "Trauma distress, flashbacks, body tension, avoidance, and hyperarousal.",
    items: [
      {
        id: "trauma-avoidance",
        symptom: "Trauma distress, avoidance",
        diagnosis: "PTSD, Complex trauma",
        example:
          "Avoiding reminders, avoiding people or places linked to the trauma, emotional numbing, distress when triggered, and reduced engagement in normal life roles.",
        therapies: [
          { label: "Trauma-Focused CBT", brief: "Structured trauma work with coping and gradual processing.", expected: "Reduced trauma symptoms and less avoidance." },
          { label: "EMDR", brief: "Helps process distressing traumatic memories.", expected: "Reduced distress intensity and improved emotional recovery." },
          { label: "Prolonged Exposure", brief: "Repeated safe exposure to trauma memories or reminders.", expected: "Less fear and less avoidance of trauma reminders." },
        ],
        medications: [
          { label: "SSRI", brief: "Sertraline 50mg/tab OD or Paroxetine 20mg/tab OD — evidence-based first-line pharmacotherapy for PTSD." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — effective for PTSD, particularly where anxiety and depression co-occur." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Specialist trauma therapy and structured trauma treatment." },
          { label: "Trauma Therapist", brief: "Focused trauma-informed support where trauma processing is central." },
        ],
      },
      {
        id: "flashbacks",
        symptom: "Flashbacks, intrusive memories",
        diagnosis: "PTSD",
        example:
          "Nightmares, vivid unwanted memories during the day, feeling like the traumatic event is happening again, and abrupt distress linked to reminders.",
        therapies: [
          { label: "EMDR", brief: "Reduces the emotional charge attached to traumatic memories.", expected: "Less intrusive distress and improved emotional stability." },
          { label: "Prolonged Exposure", brief: "Uses structured memory exposure to reduce fear responses.", expected: "Reduced avoidance and lower trauma-related distress." },
          { label: "Somatic Therapy", brief: "Addresses trauma held through bodily stress responses.", expected: "Improved body regulation and reduced trauma activation." },
        ],
        medications: [
          { label: "SSRI", brief: "Sertraline 50mg/tab OD or Paroxetine 20mg/tab OD — reduces intrusive symptoms and hyperarousal in PTSD." },
          { label: "Prazosin", brief: "1mg/tab nocte (titrate to 2–5mg) — specifically targets trauma-related nightmares; GP/psychiatrist supervision required." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Trauma-specific psychological treatment for intrusive memories." },
        ],
      },
      {
        id: "hyperarousal",
        symptom: "Hyperarousal, body tension",
        diagnosis: "Trauma-related symptoms",
        example:
          "Feeling constantly on edge, muscle tension, sleep difficulty, startling easily, scanning the environment for danger, and struggling to relax physically.",
        therapies: [
          { label: "Somatic Experiencing", brief: "Helps release physical trauma tension gradually and safely.", expected: "Less physical tension and improved nervous system settling." },
          { label: "Sensorimotor Therapy", brief: "Links body reactions with emotional triggers and regulation.", expected: "Better body awareness and reduced trauma-driven physical activation." },
          { label: "DBT", brief: "Provides distress tolerance and grounding skills.", expected: "Improved emotional containment during activation." },
        ],
        medications: [
          { label: "SSRI", brief: "Sertraline 50mg/tab OD — reduces hyperarousal and anxiety symptoms in PTSD." },
          { label: "Prazosin", brief: "1mg/tab nocte (titrate) — effective for hyperarousal and nightmares in PTSD." },
          { label: "Clonidine", brief: "0.1mg/tab BD — reduces sympathetic arousal and hypervigilance; review with prescriber." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Coping and grounding work for trauma arousal symptoms." },
          { label: "Trauma Therapist", brief: "Useful where body-based trauma symptoms are prominent." },
        ],
      },
    ],
  },
  {
    id: "social",
    label: "Social / Functional",
    note: "Relationships, isolation, work, housing, legal stress, finances, driving, and practical function.",
    items: [
      {
        id: "social-isolation",
        symptom: "Social isolation, loneliness",
        diagnosis: "Depression, Anxiety, Psychosocial stress",
        example:
          "Staying home most days, avoiding contact with family or friends, feeling disconnected, and no longer participating in community or social activities.",
        therapies: [
          { label: "IPT", brief: "Works on relationships, losses, and rebuilding social connection.", expected: "Improved social engagement and reduced isolation." },
          { label: "CBT", brief: "Targets avoidance, negative expectations, and confidence barriers.", expected: "More willingness to reconnect socially." },
          { label: "Group Therapy", brief: "Provides structured peer support and shared learning.", expected: "Improved connection and reduced loneliness." },
          { label: "Community engagement programs", brief: "Adds practical pathways back into community roles or groups.", expected: "Increased participation and routine outside the home." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — reduces depression and anxiety contributing to social withdrawal." },
          { label: "SNRI", brief: "Venlafaxine XR 75mg/cap OD — for mixed anxiety-depression with social avoidance." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Psychological support for isolation and re-engagement." },
          { label: "Social Worker", brief: "Links to community supports and practical social resources." },
        ],
      },
      {
        id: "rejection-sensitivity",
        symptom: "Rejection sensitivity",
        diagnosis: "ADHD, BPD, Trauma-related presentations",
        example:
          "Strong emotional reaction to criticism, assuming others dislike them, withdrawing after small setbacks, and feeling deeply hurt by feedback or perceived rejection.",
        therapies: [
          { label: "DBT", brief: "Improves emotional regulation when rejection is felt intensely.", expected: "Reduced emotional escalation after feedback or conflict." },
          { label: "CBT", brief: "Challenges interpretations of rejection and criticism.", expected: "More balanced reading of social situations." },
          { label: "Schema Therapy", brief: "Works on deeper beliefs around defectiveness or abandonment.", expected: "Less rejection-driven distress and more stable self-worth." },
        ],
        medications: [
          { label: "Stimulant (ADHD)", brief: "Methylphenidate IR 5mg/tab BD–TDS — reduces ADHD-driven emotional reactivity and rejection sensitivity." },
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD — for mood component or where ADHD-related rejection sensitivity overlaps with depression." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Useful where rejection sensitivity is intense or long-standing." },
        ],
      },
      {
        id: "attachment-issues",
        symptom: "Attachment issues",
        diagnosis: "Trauma, BPD, Relational difficulties",
        example:
          "Fear of abandonment, clingy or avoidant relationship patterns, difficulty trusting others, and rapidly shifting closeness or distance in relationships.",
        therapies: [
          { label: "Schema Therapy", brief: "Targets long-standing attachment-related patterns and beliefs.", expected: "Healthier relationship expectations and improved trust." },
          { label: "Psychodynamic Therapy", brief: "Explores how early relationships shape present relational patterns.", expected: "Better insight and more stable relationships." },
          { label: "MBT", brief: "Strengthens understanding of self and others in relationships.", expected: "Reduced relational misreading and less conflict." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — addresses comorbid depression and anxiety in attachment-related distress." },
          { label: "Mood stabiliser", brief: "Lamotrigine 25mg/tab OD (titrate) — for BPD-related mood instability affecting attachment; psychiatrist review." },
        ],
        referrals: [
          { label: "Clinical Psychologist", brief: "Specialist work for attachment and relational instability." },
        ],
      },
      {
        id: "relationship-conflict",
        symptom: "Relationship conflict",
        diagnosis: "Psychosocial stress, Depression, Anxiety",
        example:
          "Frequent arguments, communication breakdown, mistrust, separation stress, and interpersonal strain that worsens mood or daily function.",
        therapies: [
          { label: "IPT", brief: "Targets relationship issues contributing to distress.", expected: "Better communication and reduced relationship-related stress." },
          { label: "Couples Therapy", brief: "Supports communication and conflict resolution between partners.", expected: "Improved relationship functioning and reduced conflict." },
          { label: "Psychodynamic Therapy", brief: "Explores repetitive relationship patterns and triggers.", expected: "Increased insight into relational patterns." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — if comorbid depression or anxiety is driving conflict escalation." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for conflict, communication, and emotional patterns." },
          { label: "Relationship Therapist", brief: "Focused couples or relationship-based intervention." },
        ],
      },
      {
        id: "family-conflict",
        symptom: "Family conflict",
        diagnosis: "Family stress, Psychosocial difficulties",
        example:
          "Ongoing conflict at home, tension between parents and children, unresolved arguments, and a home environment that worsens emotional distress.",
        therapies: [
          { label: "Family Therapy", brief: "Works with family members to improve interaction and reduce conflict.", expected: "Improved family communication and less home-based tension." },
          { label: "Mediation services", brief: "Facilitates structured discussion where conflict is entrenched.", expected: "More workable agreements and reduced escalation." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — only if depression or anxiety is a contributing factor; not prescribed for conflict itself." },
        ],
        referrals: [
          { label: "Family Therapist", brief: "Family-focused therapeutic work on dynamics and communication." },
          { label: "Mediator", brief: "Structured conflict resolution support." },
        ],
      },
      {
        id: "employment-difficulty",
        symptom: "Employment difficulty, not working",
        diagnosis: "Depression, PTSD, ADHD, ABI, Psychosocial stress",
        example:
          "Unable to maintain attendance, reduced work output, difficulty coping with demands, prolonged time off work, or conflict with supervisors and workplace expectations.",
        therapies: [
          { label: "CBT", brief: "Builds coping strategies for work-related thoughts and anxiety.", expected: "Improved confidence and better functional coping around work." },
          { label: "Occupational Therapy", brief: "Assesses function and supports graded return-to-work strategies.", expected: "Better work capacity planning and task adaptation." },
          { label: "Vocational rehabilitation", brief: "Provides work-focused support, job planning, and reintegration.", expected: "More realistic return-to-work planning or vocational pathway." },
          { label: "WorkCover support", brief: "Coordinates where injury or compensation pathways are involved.", expected: "Improved access to return-to-work supports and planning." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — treat underlying depression or anxiety impairing work function." },
          { label: "Stimulant (ADHD)", brief: "Methylphenidate IR 5mg/tab BD–TDS or Lisdexamfetamine 20mg/cap OD — where ADHD is a primary barrier to work performance." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for mood, anxiety, trauma, and work confidence." },
          { label: "Occupational Therapist", brief: "Functional assessment and graded work support." },
          { label: "Rehab Provider", brief: "Vocational support and workplace planning." },
        ],
      },
      {
        id: "financial-stress",
        symptom: "Financial stress",
        diagnosis: "Psychosocial stress",
        example:
          "Worry about debt, bills, rent, and day-to-day costs, with resulting poor sleep, anxiety, conflict at home, and difficulty focusing on other treatment goals.",
        therapies: [
          { label: "Problem-Solving Therapy", brief: "Creates practical steps around financial stressors.", expected: "Improved sense of control and better financial problem management." },
          { label: "CBT", brief: "Helps manage the emotional response to financial pressure.", expected: "Reduced anxiety and improved coping despite ongoing stress." },
          { label: "Financial counselling", brief: "Provides specialist debt, budgeting, and financial advocacy support.", expected: "Clearer financial plan and reduced financial crisis pressure." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Sertraline 50mg/tab OD — if financial stress has triggered depression or anxiety disorder requiring pharmacological support." },
        ],
        referrals: [
          { label: "Financial Counsellor", brief: "Specialist help with debt, budgeting, and financial hardship." },
          { label: "Social Worker", brief: "Support around linked practical and welfare stressors." },
        ],
      },
      {
        id: "legal-stress",
        symptom: "Legal stress",
        diagnosis: "Psychosocial stress, Trauma-related stress",
        example:
          "Stress related to court, police matters, family law disputes, intervention orders, or compensation claims, often causing sleep disturbance, anxiety, and ongoing uncertainty.",
        therapies: [
          { label: "CBT", brief: "Reduces catastrophic thinking and helps manage legal-process anxiety.", expected: "Improved coping during ongoing legal stress." },
          { label: "Supportive Therapy", brief: "Provides a stabilising space during prolonged legal uncertainty.", expected: "Reduced distress and improved emotional containment." },
          { label: "Legal advice/support", brief: "Clarifies legal options, obligations, and next steps.", expected: "Improved clarity, advocacy, and reduced uncertainty." },
          { label: "Trauma therapy", brief: "Useful where the legal issue is directly trauma-linked.", expected: "Reduced trauma activation connected to legal processes." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Sertraline 50mg/tab OD — if legal stress has triggered depression or anxiety disorder; not prescribed for stress alone." },
        ],
        referrals: [
          { label: "Lawyer", brief: "Legal advice, representation, or procedural guidance." },
          { label: "Social Worker", brief: "Advocacy and support navigating systems and stressors." },
          { label: "Psychologist", brief: "Psychological support during ongoing legal stress." },
        ],
      },
      {
        id: "housing-instability",
        symptom: "Housing instability",
        diagnosis: "Psychosocial stress",
        example:
          "Homelessness, couch surfing, unsafe housing, overcrowding, frequent moves, rental insecurity, or inability to maintain a stable home routine.",
        therapies: [
          { label: "Case management", brief: "Coordinates practical housing-related supports and follow-up.", expected: "Improved stability and clearer housing support pathway." },
          { label: "Supportive counselling", brief: "Emotional support while practical housing issues are addressed.", expected: "Reduced distress and better engagement with support." },
          { label: "Housing services", brief: "Provides direct housing assistance, advocacy, and access pathways.", expected: "Improved access to safe and stable accommodation." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Fluoxetine 20mg/cap OD — if housing instability has contributed to depression or anxiety requiring treatment." },
        ],
        referrals: [
          { label: "Social Worker", brief: "Support for practical housing navigation and advocacy." },
          { label: "Housing Services", brief: "Housing access, crisis accommodation, or tenancy support." },
        ],
      },
      {
        id: "driving-anxiety",
        symptom: "Driving anxiety, driving avoidance",
        diagnosis: "Anxiety, PTSD",
        example:
          "Avoiding driving after an accident or panic episode, feeling panicky behind the wheel, limiting independence because of driving fear, or relying heavily on others for transport.",
        therapies: [
          { label: "Exposure Therapy", brief: "Uses gradual driving-related exposure to rebuild confidence.", expected: "Reduced driving avoidance and increased transport independence." },
          { label: "CBT", brief: "Addresses fear-based beliefs about driving or danger.", expected: "Improved confidence and reduced panic-related avoidance." },
          { label: "ACT", brief: "Supports driving toward important goals despite anxiety.", expected: "More functional participation where driving matters." },
          { label: "Driving rehabilitation", brief: "Assesses practical barriers and graded driving return where needed.", expected: "Safer, more structured return to driving tasks." },
        ],
        medications: [
          { label: "SSRI", brief: "Escitalopram 10mg/tab OD or Sertraline 50mg/tab OD — reduces underlying anxiety sustaining driving avoidance." },
          { label: "Beta-blocker (PRN)", brief: "Propranolol 10–40mg/tab PRN — for acute situational anxiety before driving attempts; not long-term." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Psychological treatment for driving fear and panic-related avoidance." },
          { label: "Occupational Therapist", brief: "Driving assessment and graded rehabilitation support." },
        ],
      },
      {
        id: "social-difficulty",
        symptom: "Social difficulty, communication difficulty",
        diagnosis: "ASD, Schizophrenia, ABI, Low IQ",
        example:
          "Difficulty starting conversations, poor eye contact, misunderstanding social cues, struggling to read tone or intention, and becoming socially withdrawn because interaction feels hard.",
        therapies: [
          { label: "Social Skills Training", brief: "Teaches social interaction in a structured and practical way.", expected: "Improved social confidence and more effective communication." },
          { label: "CBT", brief: "Supports confidence and anxiety management around social contact.", expected: "Reduced avoidance and improved social participation." },
          { label: "Occupational Therapy", brief: "Supports practical communication and function in daily life settings.", expected: "Better day-to-day social functioning." },
        ],
        medications: [
          { label: "SSRI (anxiety component)", brief: "Fluoxetine 10mg/cap OD — for anxiety driving social avoidance, particularly in ASD presentations." },
          { label: "Antipsychotic (Schizophrenia)", brief: "Risperidone 0.5–1mg/tab OD — for psychosis-related social withdrawal; psychiatrist management required." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for social confidence and interaction patterns." },
          { label: "Occupational Therapist", brief: "Practical communication and functional skill-building." },
        ],
      },
    ],
  },
  {
    id: "substance",
    label: "Substance / Eating",
    note: "Motivation to change and specific substance or coping-related use patterns.",
    items: [
      {
        id: "alcohol-use",
        symptom: "Alcohol use",
        diagnosis: "Alcohol use disorder, Harmful use, Coping-related alcohol use",
        example:
          "Using alcohol to switch off, sleep, numb trauma, reduce anxiety, or cope with loneliness; increasing frequency or amount over time; ongoing consequences despite continued use.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Builds readiness and commitment for change.", expected: "Increased willingness to reduce or stop alcohol use." },
          { label: "CBT", brief: "Identifies triggers, thinking patterns, and alternative coping strategies.", expected: "Reduced relapse triggers and improved alcohol coping plan." },
          { label: "ACT", brief: "Builds values-based choices instead of using alcohol to escape distress.", expected: "Greater alignment with recovery goals." },
          { label: "AOD programs", brief: "Provides structured alcohol treatment and recovery support.", expected: "Improved engagement in alcohol-focused care." },
        ],
        medications: [
          { label: "Naltrexone", brief: "50mg/tab OD — reduces alcohol craving and reward; suitable after detox." },
          { label: "Acamprosate", brief: "666mg/tab TDS — supports abstinence by reducing post-withdrawal discomfort; start after detox." },
          { label: "Disulfiram (supervised)", brief: "250mg/tab OD — aversion medication; requires supervised administration and GP oversight." },
        ],
        referrals: [
          { label: "AOD Counsellor", brief: "Alcohol and other drug counselling and relapse prevention." },
          { label: "Psychologist", brief: "Supports alcohol-related coping, trauma, or mood drivers." },
        ],
      },
      {
        id: "smoking",
        symptom: "Smoking / nicotine use",
        diagnosis: "Nicotine dependence",
        example:
          "Regular smoking or nicotine use to manage stress, boredom, concentration, or habit; strong cravings; repeated unsuccessful quit attempts.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Builds commitment to smoking reduction or quitting.", expected: "Improved readiness to quit nicotine." },
          { label: "CBT", brief: "Targets triggers, habits, and alternative behavioural responses.", expected: "Reduced cue-driven smoking and better quit coping." },
          { label: "Smoking cessation support", brief: "Provides behavioural and practical quit support.", expected: "Higher chance of sustained quit effort." },
        ],
        medications: [
          { label: "Varenicline (Champix)", brief: "0.5mg/tab OD for 3 days, then 0.5mg BD for 4 days, then 1mg BD for 12 weeks — most effective quit medication." },
          { label: "Bupropion", brief: "150mg/tab OD for 3 days, then 150mg BD — start 1–2 weeks before quit date; avoid if seizure history." },
          { label: "NRT", brief: "Nicotine patch 21mg/24h or nicotine gum 2–4mg PRN — suitable as standalone or combined with other medications." },
        ],
        referrals: [
          { label: "GP", brief: "Medication support and smoking cessation planning." },
          { label: "AOD Counsellor", brief: "Behavioural support for nicotine dependence." },
        ],
      },
      {
        id: "caffeine",
        symptom: "Caffeine overuse",
        diagnosis: "Problematic caffeine use, Anxiety-related aggravation",
        example:
          "Heavy use of coffee, energy drinks, or caffeine tablets contributing to anxiety, poor sleep, jitteriness, palpitations, or dependence on caffeine to function.",
        therapies: [
          { label: "Psychoeducation", brief: "Explains the link between caffeine, anxiety, and sleep disruption.", expected: "Better insight into the effect of caffeine on symptoms." },
          { label: "CBT", brief: "Helps shift habits and expectations around caffeine reliance.", expected: "Reduced caffeine intake and improved anxiety management." },
          { label: "Sleep hygiene support", brief: "Addresses energy regulation and sleep routines without relying on stimulants.", expected: "Improved sleep and reduced stimulant dependence." },
        ],
        medications: [
          { label: "Melatonin", brief: "2mg/tab nocte — for associated sleep disruption from caffeine use; supports circadian regulation." },
          { label: "SSRI (if comorbid anxiety)", brief: "Escitalopram 10mg/tab OD — if caffeine use is driven by or worsening an anxiety disorder." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for anxiety-linked caffeine habits and behaviour change." },
          { label: "GP", brief: "Review of physical symptoms and support to taper stimulant use." },
        ],
      },
      {
        id: "thc",
        symptom: "THC / cannabis use",
        diagnosis: "Cannabis use disorder, Coping-related cannabis use",
        example:
          "Using cannabis to relax, sleep, cope with trauma, or numb emotions; reduced motivation; memory or concentration issues; ongoing use despite problems.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Builds readiness to reduce or stop cannabis use.", expected: "Improved motivation for behaviour change." },
          { label: "CBT", brief: "Targets triggers, routines, and replacement coping strategies.", expected: "Reduced cannabis use and improved coping alternatives." },
          { label: "ACT", brief: "Helps respond to cravings without automatic use.", expected: "More values-based decisions and less reactive use." },
          { label: "AOD programs", brief: "Provides structured support for cannabis-related change.", expected: "Improved engagement in recovery planning." },
        ],
        medications: [
          { label: "SSRI (comorbid anxiety/depression)", brief: "Sertraline 50mg/tab OD — treats comorbid mood or anxiety driving coping-related cannabis use." },
          { label: "Note", brief: "No TGA-approved cannabis-specific pharmacotherapy is currently available; treatment focuses on managing comorbidities." },
        ],
        referrals: [
          { label: "AOD Counsellor", brief: "Specialist counselling for cannabis use and relapse prevention." },
          { label: "Psychologist", brief: "Support where cannabis use is linked to mood, trauma, or coping." },
        ],
      },
      {
        id: "benzo",
        symptom: "Benzo use",
        diagnosis: "Benzodiazepine dependence, Coping-related sedative use",
        example:
          "Using benzodiazepines to sleep, calm down, or get through anxiety; increasing reliance; fear of coping without them; possible tolerance or dependence.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Supports readiness for reduction and safer alternatives.", expected: "Improved willingness to review benzodiazepine use." },
          { label: "CBT", brief: "Builds non-medication strategies for anxiety and sleep.", expected: "Reduced reliance on benzos for coping." },
          { label: "Medication review / taper plan", brief: "Provides structured and safer reduction planning.", expected: "Safer dose reduction and improved long-term coping." },
        ],
        medications: [
          { label: "Diazepam taper (substitution)", brief: "Convert short-acting benzo to diazepam equivalent, then taper 5–10% per 1–2 weeks — GP-managed taper plan." },
          { label: "SSRI (underlying anxiety)", brief: "Sertraline 50mg/tab OD — addresses underlying anxiety disorder sustaining benzo reliance during taper." },
        ],
        referrals: [
          { label: "GP", brief: "Medication review, taper planning, and physical monitoring." },
          { label: "Psychiatrist", brief: "Useful for complex dependence, risk, or diagnostic overlap." },
          { label: "AOD Counsellor", brief: "Support for sedative dependence and relapse prevention." },
        ],
      },
      {
        id: "opioid",
        symptom: "Opioid use",
        diagnosis: "Opioid dependence, Pain-related opioid reliance",
        example:
          "Using opioids beyond the original pain plan, increased tolerance, relying on opioids to manage both pain and emotional distress, or difficulty reducing dose.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Helps explore readiness for safer opioid management.", expected: "Improved engagement with change and dose review." },
          { label: "CBT for pain coping", brief: "Builds non-opioid coping skills for pain-related distress.", expected: "Reduced opioid reliance and better pain self-management." },
          { label: "Medication review / pain plan", brief: "Provides structured opioid review and safer management.", expected: "Safer prescribing and clearer pain strategy." },
          { label: "AOD programs", brief: "Supports where opioid dependence is established.", expected: "Improved recovery engagement and harm reduction." },
        ],
        medications: [
          { label: "Buprenorphine/naloxone (Suboxone)", brief: "2mg/0.5mg SL OD–QID — opioid substitution therapy; prescriber accreditation required." },
          { label: "Methadone", brief: "Specialist prescribing only — for opioid dependence where buprenorphine is unsuitable." },
          { label: "Naltrexone", brief: "50mg/tab OD — for relapse prevention after opioid detox; must be opioid-free for 7–10 days first." },
        ],
        referrals: [
          { label: "GP", brief: "Medication review and coordination of opioid management." },
          { label: "Pain Specialist", brief: "Useful for complex pain and opioid reliance." },
          { label: "AOD Counsellor", brief: "Dependence-focused support and relapse prevention." },
        ],
      },
      {
        id: "low-motivation-change",
        symptom: "Low motivation to change",
        diagnosis: "Substance use, Behaviour change difficulty",
        example:
          "Not ready to stop, ambivalent about cutting down, minimising the impact of use, or repeatedly intending to change without following through.",
        therapies: [
          { label: "Motivational Interviewing", brief: "Explores ambivalence and strengthens internal motivation.", expected: "Increased readiness and clearer change goals." },
          { label: "CBT", brief: "Identifies behaviour patterns and practical barriers to change.", expected: "Improved action planning and coping with triggers." },
          { label: "ACT", brief: "Links behaviour change to personal values and long-term goals.", expected: "More consistent follow-through despite discomfort." },
          { label: "AOD programs", brief: "Adds structure and support when change is difficult to sustain alone.", expected: "Improved engagement with formal support." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Fluoxetine 20mg/cap OD or Sertraline 50mg/tab OD — if depression or anxiety is reducing motivation to change." },
        ],
        referrals: [
          { label: "AOD Counsellor", brief: "Motivation-building and structured behaviour change support." },
          { label: "Psychologist", brief: "Useful where motivation is affected by mood, trauma, or avoidance." },
        ],
      },
    ],
  },
  {
    id: "adhd",
    label: "ADHD",
    note: "ADHD symptoms separated by presentation, with targeted treatment approaches.",
    items: [
      {
        id: "adhd-inattention",
        symptom: "Poor attention, distractibility",
        diagnosis: "ADHD",
        example:
          "Losing focus easily, forgetting what they were doing, starting tasks without finishing them, missing details, and struggling to stay engaged with routine tasks.",
        therapies: [
          { label: "ADHD-informed CBT", brief: "Builds practical strategies for attention, planning, and follow-through.", expected: "Improved task completion and attention management." },
          { label: "Executive functioning coaching", brief: "Supports planning, routine, prioritising, and organisation.", expected: "Better day-to-day structure and reduced chaos." },
          { label: "Medication review", brief: "Assesses whether stimulant or non-stimulant medication may help.", expected: "Improved concentration and reduced ADHD burden where indicated." },
          { label: "Psychoeducation", brief: "Improves understanding of how ADHD affects daily life.", expected: "Greater self-awareness and more realistic strategy use." },
        ],
        medications: [
          { label: "Methylphenidate IR", brief: "5mg/tab BD–TDS (titrate to response) — short-acting stimulant; taken with meals." },
          { label: "Lisdexamfetamine", brief: "20mg/cap OD (titrate to 40–70mg) — long-acting stimulant; once daily dosing." },
          { label: "Dexamfetamine", brief: "5mg/tab BD–TDS — short-acting stimulant; alternative to methylphenidate." },
          { label: "Atomoxetine", brief: "18mg/cap OD (titrate to 80–100mg) — non-stimulant; suitable where stimulants are contraindicated." },
        ],
        referrals: [
          { label: "Psychiatrist", brief: "Diagnostic review and medication management where needed." },
          { label: "Psychologist", brief: "CBT and practical coping strategies for ADHD symptoms." },
        ],
      },
      {
        id: "adhd-impulsivity",
        symptom: "Impulsivity, poor self-control",
        diagnosis: "ADHD",
        example:
          "Interrupting, acting before thinking, emotional outbursts, impulsive spending, risk-taking, or making quick decisions without considering consequences.",
        therapies: [
          { label: "CBT", brief: "Builds awareness of triggers and delayed response skills.", expected: "Reduced impulsive behaviour and better pause-and-plan control." },
          { label: "DBT-informed skills", brief: "Adds emotional regulation and distress tolerance skills.", expected: "Improved self-control under stress." },
          { label: "Medication review", brief: "Reviews whether medication may reduce impulsivity.", expected: "Improved behavioural regulation where medication is appropriate." },
        ],
        medications: [
          { label: "Methylphenidate IR", brief: "5mg/tab BD–TDS (titrate) — reduces impulsivity and hyperactivity in ADHD." },
          { label: "Lisdexamfetamine", brief: "20mg/cap OD (titrate) — long-acting stimulant with sustained impulse control effect." },
          { label: "Dexamfetamine", brief: "5mg/tab BD–TDS — effective for impulsivity; shorter duration than lisdexamfetamine." },
          { label: "Clonidine", brief: "0.1mg/tab BD — adjunct for impulsivity and emotional dysregulation in ADHD; not a stimulant." },
        ],
        referrals: [
          { label: "Psychiatrist", brief: "Medication review and diagnostic clarification." },
          { label: "Psychologist", brief: "Behavioural and emotional regulation strategies." },
        ],
      },
      {
        id: "adhd-rejection",
        symptom: "Rejection sensitivity, emotional lability",
        diagnosis: "ADHD",
        example:
          "Feeling devastated by criticism, overreacting to minor feedback, quickly becoming upset, and withdrawing or arguing after perceived rejection.",
        therapies: [
          { label: "CBT", brief: "Challenges interpretations of criticism and rejection.", expected: "Less emotional spiralling after feedback." },
          { label: "DBT-informed skills", brief: "Improves tolerance of emotional surges and rejection triggers.", expected: "Improved emotional steadiness and less conflict." },
          { label: "Psychoeducation", brief: "Normalises how ADHD can affect emotional intensity.", expected: "Better self-understanding and less shame." },
        ],
        medications: [
          { label: "Methylphenidate IR", brief: "5mg/tab BD–TDS — reduces overall ADHD burden, including emotional reactivity." },
          { label: "Clonidine", brief: "0.1mg/tab BD — specifically targets emotional dysregulation and rejection sensitivity in ADHD." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for emotional regulation and rejection sensitivity." },
        ],
      },
    ],
  },
  {
    id: "asd",
    label: "ASD",
    note: "Autism-related symptoms with practical and supportive treatment approaches.",
    items: [
      {
        id: "asd-social",
        symptom: "Social communication difficulty",
        diagnosis: "ASD",
        example:
          "Difficulty reading social cues, interpreting tone literally, uncertainty in conversation flow, and problems knowing when to speak or how to respond socially.",
        therapies: [
          { label: "Social Skills Training", brief: "Teaches conversation, turn-taking, cue reading, and interaction skills.", expected: "Improved confidence and more effective social interaction." },
          { label: "Speech Pathology support", brief: "Helps with communication style, understanding, and expressive strategies.", expected: "Improved communication clarity and social understanding." },
          { label: "Psychoeducation", brief: "Builds insight into autism-related communication patterns.", expected: "Better self-understanding and reduced shame." },
        ],
        medications: [
          { label: "SSRI (anxiety component)", brief: "Fluoxetine 10mg/cap OD — for anxiety driving social avoidance in ASD; start low and titrate slowly." },
          { label: "Note", brief: "No medication directly improves ASD social communication; medication targets comorbid anxiety, depression, or irritability only." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Support for social understanding, anxiety, and coping." },
          { label: "Speech Pathologist", brief: "Communication-focused support." },
        ],
      },
      {
        id: "asd-sensory",
        symptom: "Sensory sensitivity, overwhelm",
        diagnosis: "ASD",
        example:
          "Distress with noise, crowds, touch, lights, food textures, or routine changes, leading to shutdowns, avoidance, or irritability.",
        therapies: [
          { label: "Occupational Therapy", brief: "Supports sensory regulation and environmental adjustment.", expected: "Reduced overwhelm and better sensory coping." },
          { label: "Environmental modification", brief: "Adapts settings, routines, and triggers to reduce overload.", expected: "Improved day-to-day functioning and less shutdown." },
          { label: "Psychoeducation", brief: "Helps explain sensory patterns and practical coping options.", expected: "Improved self-awareness and planning." },
        ],
        medications: [
          { label: "Melatonin", brief: "2mg/tab nocte — for sleep disruption related to sensory sensitivity and arousal dysregulation in ASD." },
          { label: "SSRI (if anxiety comorbid)", brief: "Fluoxetine 10mg/cap OD — where anxiety is exacerbating sensory sensitivity; start low." },
        ],
        referrals: [
          { label: "Occupational Therapist", brief: "Sensory regulation and environmental strategy support." },
        ],
      },
      {
        id: "asd-rigidity",
        symptom: "Rigidity, distress with change",
        diagnosis: "ASD",
        example:
          "Becoming highly distressed when routines change, difficulty adapting to unexpected plans, strong need for predictability, and conflict around transitions.",
        therapies: [
          { label: "CBT adapted for ASD", brief: "Uses concrete strategies to manage change-related distress.", expected: "Improved flexibility and reduced change-related anxiety." },
          { label: "Routine planning", brief: "Builds visual or structured plans for predictable transitions.", expected: "Smoother adaptation and less distress during change." },
          { label: "Occupational Therapy", brief: "Supports practical adjustment strategies and daily structure.", expected: "Improved tolerance for routine variation." },
        ],
        medications: [
          { label: "SSRI", brief: "Fluoxetine 10mg/cap OD — may reduce rigidity and anxiety related to routine disruption in ASD; start low, titrate slowly." },
          { label: "Risperidone", brief: "0.5mg/tab OD — for significant irritability or agitation with change; psychiatrist oversight required." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Adapted therapy for anxiety, rigidity, and coping." },
          { label: "Occupational Therapist", brief: "Structure and transition support." },
        ],
      },
    ],
  },
  {
    id: "low-iq",
    label: "Low IQ",
    note: "Cognitive and adaptive difficulties requiring simplified, practical, and supportive approaches.",
    items: [
      {
        id: "lowiq-comprehension",
        symptom: "Poor comprehension, slow processing",
        diagnosis: "Low IQ, Intellectual disability",
        example:
          "Difficulty understanding complex instructions, needing repeated explanation, slower learning pace, and getting confused with multi-step tasks.",
        therapies: [
          { label: "Simplified psychoeducation", brief: "Uses clear, concrete language and repetition.", expected: "Better understanding of health issues and treatment steps." },
          { label: "Supportive therapy", brief: "Focuses on simple emotional support and practical guidance.", expected: "Improved engagement and reduced confusion." },
          { label: "Occupational Therapy", brief: "Helps build practical daily living and task-management skills.", expected: "Improved independence and daily functioning." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Fluoxetine 10mg/cap OD — for comorbid anxiety or depression; use with caution and lower starting doses given cognitive profile." },
          { label: "Note", brief: "No medication improves cognitive processing or IQ; pharmacotherapy targets comorbid mental health conditions only." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Supportive or adapted therapy using simplified approaches." },
          { label: "Occupational Therapist", brief: "Practical functioning and adaptive skill-building." },
        ],
      },
      {
        id: "lowiq-adl",
        symptom: "Adaptive functioning difficulty",
        diagnosis: "Low IQ, Intellectual disability",
        example: "Trouble managing money, appointments, shopping, transport, cooking, or medications without support.",
        therapies: [
          { label: "Skills training", brief: "Teaches daily living tasks in small, concrete steps.", expected: "Improved independence in practical daily tasks." },
          { label: "Case management", brief: "Coordinates services and practical supports.", expected: "Better consistency of supports and less functional breakdown." },
          { label: "Occupational Therapy", brief: "Assesses and builds adaptive functioning skills.", expected: "Improved practical life management." },
        ],
        medications: [
          { label: "Note", brief: "No medication directly improves adaptive functioning; pharmacotherapy is indicated only for comorbid mental health conditions as directed by GP or psychiatrist." },
        ],
        referrals: [
          { label: "Occupational Therapist", brief: "Assessment and support for adaptive functioning." },
          { label: "Social Worker", brief: "Coordination of community and disability supports." },
        ],
      },
      {
        id: "lowiq-vulnerability",
        symptom: "High vulnerability, poor judgement",
        diagnosis: "Low IQ, Intellectual disability",
        example:
          "Easily influenced by others, difficulty recognising risk, trouble understanding consequences, and vulnerability to exploitation or unsafe situations.",
        therapies: [
          { label: "Supportive counselling", brief: "Provides guidance, reassurance, and practical safety discussion.", expected: "Improved confidence and better help-seeking." },
          { label: "Safety skills training", brief: "Teaches concrete strategies for recognising and responding to risk.", expected: "Improved personal safety awareness." },
          { label: "Case management", brief: "Adds oversight and support coordination where risks are ongoing.", expected: "Reduced vulnerability and more reliable support network." },
        ],
        medications: [
          { label: "SSRI (if comorbid)", brief: "Fluoxetine 10mg/cap OD — for comorbid anxiety or depression increasing vulnerability; use low doses and monitor carefully." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Supportive therapy using concrete and simplified methods." },
          { label: "Social Worker", brief: "Practical safeguarding and support coordination." },
        ],
      },
    ],
  },
  {
    id: "abi",
    label: "ABI",
    note: "Acquired brain injury symptoms with cognitive, behavioural, and rehabilitation-oriented treatment approaches.",
    items: [
      {
        id: "abi-memory",
        symptom: "Memory and concentration difficulty",
        diagnosis: "ABI",
        example:
          "Forgetting recent information, losing track of tasks, poor attention span, difficulty holding instructions in mind, and mental fatigue during everyday activities.",
        therapies: [
          { label: "Neuropsychological strategies", brief: "Uses memory aids, structured routines, and compensatory strategies.", expected: "Improved day-to-day memory management and concentration support." },
          { label: "Occupational Therapy", brief: "Supports practical adaptations for daily cognitive functioning.", expected: "Better daily task performance and reduced cognitive overload." },
          { label: "Cognitive rehabilitation", brief: "Targets structured recovery or compensation for cognitive deficits.", expected: "Improved cognitive function or more effective coping with deficits." },
        ],
        medications: [
          { label: "SSRI (if depression comorbid)", brief: "Sertraline 50mg/tab OD — treats post-ABI depression which significantly worsens cognitive performance." },
          { label: "Donepezil (specialist)", brief: "5mg/tab nocte — off-label for post-ABI cognitive impairment; specialist review required." },
          { label: "Methylphenidate IR (specialist)", brief: "5mg/tab BD — for post-ABI attention deficits; specialist-supervised prescribing." },
        ],
        referrals: [
          { label: "Neuropsychologist", brief: "Assessment and treatment planning for cognitive deficits after ABI." },
          { label: "Occupational Therapist", brief: "Function-based support for daily activities and routines." },
        ],
      },
      {
        id: "abi-fatigue",
        symptom: "Fatigue, reduced mental endurance",
        diagnosis: "ABI",
        example:
          "Becoming mentally exhausted quickly, needing frequent breaks, worsening concentration later in the day, and reduced tolerance for work or busy environments.",
        therapies: [
          { label: "Energy pacing", brief: "Teaches how to structure activity and rest to avoid overload.", expected: "More stable daily functioning and fewer fatigue crashes." },
          { label: "Occupational Therapy", brief: "Supports graded activity planning and functional pacing.", expected: "Improved endurance management." },
          { label: "Rehabilitation planning", brief: "Builds realistic expectations and sustainable task structure.", expected: "Improved daily participation without excessive burnout." },
        ],
        medications: [
          { label: "SSRI (if depression driving fatigue)", brief: "Sertraline 50mg/tab OD — post-ABI depression is a major contributor to fatigue; treat first." },
          { label: "Modafinil (specialist)", brief: "100mg/tab OD — for post-ABI fatigue where depression is excluded; specialist review required." },
        ],
        referrals: [
          { label: "Occupational Therapist", brief: "Supports pacing, activity grading, and functional planning." },
          { label: "Rehab Physician", brief: "Useful where broader rehabilitation oversight is needed." },
        ],
      },
      {
        id: "abi-behaviour",
        symptom: "Irritability, poor frustration tolerance, emotional change",
        diagnosis: "ABI",
        example:
          "Short temper, getting upset more easily than before, poor tolerance for noise or pressure, and emotional outbursts that impact relationships and work.",
        therapies: [
          { label: "CBT adapted for ABI", brief: "Uses simplified, practical emotion and behaviour strategies.", expected: "Improved emotional control and reduced conflict." },
          { label: "Behaviour support planning", brief: "Identifies triggers and practical support responses.", expected: "Reduced escalation and more predictable support." },
          { label: "Psychoeducation for family/supports", brief: "Helps others understand ABI-related emotional change.", expected: "Better support response and less interpersonal strain." },
        ],
        medications: [
          { label: "SSRI", brief: "Sertraline 50mg/tab OD — reduces emotional lability and irritability post-ABI." },
          { label: "Beta-blocker", brief: "Propranolol 40mg/tab OD — for aggression and irritability after ABI; review with prescriber." },
          { label: "Mood stabiliser (specialist)", brief: "Valproate 200mg/tab BD — for severe post-ABI emotional dysregulation; specialist supervision required." },
        ],
        referrals: [
          { label: "Psychologist", brief: "Emotional regulation support adapted for ABI." },
          { label: "Neuropsychologist", brief: "Useful where ABI-related behavioural change is prominent." },
        ],
      },
    ],
  },
];

function buildInitialSelections() {
  const sel = {};
  appData.forEach((domain) => {
    domain.items.forEach((item) => {
      sel[item.id] = {
        target: false,
        therapies: new Array(item.therapies.length).fill(false),
        medications: new Array(item.medications.length).fill(false),
        referrals: new Array(item.referrals.length).fill(false),
      };
    });
  });
  return sel;
}

function getUniqueDiagnoses() {
  const all = [];
  appData.forEach((domain) => {
    domain.items.forEach((item) => {
      item.diagnosis.split(",").forEach((d) => {
        const trimmed = d.trim();
        if (trimmed && !all.includes(trimmed)) all.push(trimmed);
      });
    });
  });
  return all.sort((a, b) => a.localeCompare(b));
}

class MentalHealthPlan extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: appData[0].id,
      searchQuery: "",
      diagnosisFilter: "",
      domainFilter: "",
      expandAll: false,
      collapsedItems: {},
      selections: buildInitialSelections(),
      copied: false,
    };
  }

  matchesFilters(domain, item) {
    const { searchQuery, diagnosisFilter, domainFilter } = this.state;
    const q = searchQuery.trim().toLowerCase();
    const blob = [
      domain.label,
      item.symptom,
      item.diagnosis,
      item.example,
      ...item.therapies.map((t) => `${t.label} ${t.brief} ${t.expected}`),
      ...item.medications.map((m) => `${m.label} ${m.brief}`),
      ...item.referrals.map((r) => `${r.label} ${r.brief}`),
    ]
      .join(" ")
      .toLowerCase();

    const qMatch = !q || blob.includes(q);
    const diagMatch =
      !diagnosisFilter ||
      item.diagnosis
        .split(",")
        .map((v) => v.trim())
        .includes(diagnosisFilter);
    const domainMatch = !domainFilter || domain.id === domainFilter;
    return qMatch && diagMatch && domainMatch;
  }

  handleTargetChange(itemId) {
    this.setState((prev) => ({
      selections: {
        ...prev.selections,
        [itemId]: {
          ...prev.selections[itemId],
          target: !prev.selections[itemId].target,
        },
      },
    }));
  }

  handleTherapyChange(itemId, idx) {
    this.setState((prev) => {
      const therapies = [...prev.selections[itemId].therapies];
      therapies[idx] = !therapies[idx];
      return {
        selections: {
          ...prev.selections,
          [itemId]: { ...prev.selections[itemId], therapies },
        },
      };
    });
  }

  handleMedicationChange(itemId, idx) {
    this.setState((prev) => {
      const medications = [...prev.selections[itemId].medications];
      medications[idx] = !medications[idx];
      return {
        selections: {
          ...prev.selections,
          [itemId]: { ...prev.selections[itemId], medications },
        },
      };
    });
  }

  handleReferralChange(itemId, idx) {
    this.setState((prev) => {
      const referrals = [...prev.selections[itemId].referrals];
      referrals[idx] = !referrals[idx];
      return {
        selections: {
          ...prev.selections,
          [itemId]: { ...prev.selections[itemId], referrals },
        },
      };
    });
  }

  clearAll() {
    this.setState({ selections: buildInitialSelections() });
  }

  toggleItemCollapse(itemId) {
    this.setState((prev) => ({
      collapsedItems: {
        ...prev.collapsedItems,
        [itemId]: !prev.collapsedItems[itemId],
      },
    }));
  }

  collapseAllItems() {
    const collapsed = {};
    appData.forEach((d) => { d.items.forEach((item) => { collapsed[item.id] = true; }); });
    this.setState({ collapsedItems: collapsed });
  }

  getSelectionsList() {
    const { selections } = this.state;
    const result = [];
    appData.forEach((domain) => {
      domain.items.forEach((item) => {
        const sel = selections[item.id];
        const checkedTherapies = item.therapies.filter((_, i) => sel.therapies[i]);
        const checkedMedications = item.medications.filter((_, i) => sel.medications[i]);
        const checkedReferrals = item.referrals.filter((_, i) => sel.referrals[i]);
        if (sel.target || checkedTherapies.length || checkedMedications.length || checkedReferrals.length) {
          result.push({
            domain: domain.label,
            symptom: item.symptom,
            therapies: checkedTherapies,
            medications: checkedMedications,
            referrals: checkedReferrals,
          });
        }
      });
    });
    return result;
  }

  copySummary() {
    const selections = this.getSelectionsList();
    if (!selections.length) {
      alert("Nothing selected yet.");
      return;
    }
    const text = selections
      .map((entry) => {
        let chunk = `${entry.domain} - ${entry.symptom}`;
        if (entry.therapies.length) {
          chunk += "\nTherapy / support options:";
          entry.therapies.forEach((t) => {
            chunk += `\n- ${t.label}: ${t.brief} Expected outcome: ${t.expected}`;
          });
        }
        if (entry.medications.length) {
          chunk += "\nMedication options:";
          entry.medications.forEach((m) => {
            chunk += `\n- ${m.label}: ${m.brief}`;
          });
        }
        if (entry.referrals.length) {
          chunk += "\nReferral to:";
          entry.referrals.forEach((r) => {
            chunk += `\n- ${r.label}: ${r.brief}`;
          });
        }
        return chunk;
      })
      .join("\n\n");

    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 1200);
      })
      .catch(() => alert("Could not copy summary."));
  }

  renderCard(item) {
    const { selections, collapsedItems } = this.state;
    const sel = selections[item.id];
    const isCollapsed = !!collapsedItems[item.id];

    return (
      <article
        key={item.id}
        className="border border-slate-200 rounded-2xl bg-gradient-to-b from-white to-[#f9fbff] overflow-hidden"
      >
        {/* Card Header */}
        <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-start px-4 pt-4 pb-2.5 border-b border-[#edf2f9]">
          <input
            type="checkbox"
            className="w-5 h-5 mt-0.5 accent-sky-700 cursor-pointer"
            checked={sel.target}
            onChange={() => this.handleTargetChange(item.id)}
          />
          <div>
            <h3 className="m-0 text-lg font-bold leading-tight">{item.symptom}</h3>
            <span className="mt-1.5 inline-block px-2.5 py-1 rounded-full bg-sky-100 text-sky-900 text-xs font-bold">
              {item.diagnosis}
            </span>
            {!isCollapsed && (
              <p className="mt-2 text-slate-500 text-sm leading-relaxed">{item.example}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => this.toggleItemCollapse(item.id)}
            className="text-xs text-sky-600 font-bold px-3 py-1.5 rounded-lg border border-sky-200 hover:bg-sky-50 transition-colors whitespace-nowrap mt-0.5"
          >
            {isCollapsed ? "Expand" : "Collapse"}
          </button>
        </div>

        {!isCollapsed && (
          <>
            {/* Therapy + Referral columns */}
            <div className="px-4 pt-3.5 pb-0 grid grid-cols-1 lg:grid-cols-[1.25fr_0.95fr] gap-4">
              {/* Therapies */}
              <div>
                <div className="text-xs uppercase tracking-widest text-teal-700 mb-2.5 font-bold">
                  Therapy / support options
                </div>
                <div className="grid gap-2.5">
                  {item.therapies.map((therapy, idx) => (
                    <label
                      key={idx}
                      className="border border-slate-200 bg-[#f8fbff] rounded-2xl p-3 grid grid-cols-[auto_1fr] gap-2.5 items-start cursor-pointer hover:bg-sky-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 accent-sky-700 cursor-pointer"
                        checked={sel.therapies[idx]}
                        onChange={() => this.handleTherapyChange(item.id, idx)}
                      />
                      <div>
                        <div className="font-bold mb-1 text-sm">{therapy.label}</div>
                        <div className="text-slate-500 text-xs leading-snug">{therapy.brief}</div>
                        <div className="text-slate-500 text-xs leading-snug mt-0.5">
                          <strong>Expected outcome:</strong> {therapy.expected}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Referrals */}
              <div>
                <div className="text-xs uppercase tracking-widest text-teal-700 mb-2.5 font-bold">
                  Referral to
                </div>
                <div className="grid gap-2.5">
                  {item.referrals.map((referral, idx) => (
                    <label
                      key={idx}
                      className="border border-slate-200 bg-[#f8fbff] rounded-2xl p-3 grid grid-cols-[auto_1fr] gap-2.5 items-start cursor-pointer hover:bg-sky-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 accent-sky-700 cursor-pointer"
                        checked={sel.referrals[idx]}
                        onChange={() => this.handleReferralChange(item.id, idx)}
                      />
                      <div>
                        <div className="font-bold mb-1 text-sm">{referral.label}</div>
                        <div className="text-slate-500 text-xs leading-snug">{referral.brief}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Medications — full-width row below */}
            <div className="px-4 pt-3.5 pb-4">
              <div className="text-xs uppercase tracking-widest text-violet-700 mb-2.5 font-bold">
                Medication options
              </div>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {item.medications.map((med, idx) => (
                  <label
                    key={idx}
                    className="border border-violet-100 bg-violet-50 rounded-2xl p-3 grid grid-cols-[auto_1fr] gap-2.5 items-start cursor-pointer hover:bg-violet-100 transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-violet-700 cursor-pointer"
                      checked={sel.medications[idx]}
                      onChange={() => this.handleMedicationChange(item.id, idx)}
                    />
                    <div>
                      <div className="font-bold mb-1 text-sm text-violet-900">{med.label}</div>
                      <div className="text-violet-700 text-xs leading-snug">{med.brief}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </article>
    );
  }

  render() {
    const { activeTab, searchQuery, diagnosisFilter, domainFilter, expandAll, copied } = this.state;
    const diagnoses = getUniqueDiagnoses();
    const summaryList = this.getSelectionsList();

    return (
      <div className="min-h-screen bg-gradient-to-b from-[#eef4ff] to-[#f3f6fb] font-sans text-slate-900">
        <div className="max-w-[1680px] mx-auto px-4 py-4">

          {/* Top Bar */}
          <section className="bg-gradient-to-br from-slate-950 to-sky-900 text-white rounded-3xl px-6 py-6 shadow-lg mb-4">
            <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">
              Whole-person Mental Health Plan
            </h1>
            <p className="text-sky-100 max-w-4xl leading-relaxed text-sm">
              Select target symptoms, therapy options, medication options, and referral pathways across mental health,
              psychosocial, substance use, ADHD, ASD, Low IQ, and ABI domains. The summary panel builds a clean,
              printable care-planning output focused on selected symptoms, brief therapy explanations, medication
              options, referral to, and expected outcomes.
            </p>
          </section>

          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.9fr_auto_auto_auto] gap-3 my-4 items-center">
            <input
              type="text"
              placeholder="Search symptoms, therapy, medication, referral, diagnosis..."
              value={searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              className="rounded-xl border border-slate-200 px-3.5 py-3 text-sm bg-white text-slate-900 outline-none focus:ring-2 focus:ring-sky-400"
            />
            <select
              value={diagnosisFilter}
              onChange={(e) => this.setState({ diagnosisFilter: e.target.value })}
              className="rounded-xl border border-slate-200 px-3.5 py-3 text-sm bg-white text-slate-900 outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="">All diagnoses</option>
              {diagnoses.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <select
              value={domainFilter}
              onChange={(e) => {
                const val = e.target.value;
                this.setState({ domainFilter: val, activeTab: val || activeTab });
              }}
              className="rounded-xl border border-slate-200 px-3.5 py-3 text-sm bg-white text-slate-900 outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="">All tabs</option>
              {appData.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => this.setState((prev) => ({ expandAll: !prev.expandAll }))}
              className="rounded-xl border border-sky-700 bg-sky-700 text-white px-4 py-3 text-sm font-bold cursor-pointer hover:bg-sky-800 transition-colors whitespace-nowrap"
            >
              {expandAll ? "Collapse tabs" : "Expand all tabs"}
            </button>
            <button
              type="button"
              onClick={() => this.collapseAllItems()}
              className="rounded-xl border border-slate-400 bg-slate-100 text-slate-700 px-4 py-3 text-sm font-bold cursor-pointer hover:bg-slate-200 transition-colors whitespace-nowrap"
            >
              Collapse all
            </button>
            <button
              type="button"
              onClick={() => this.clearAll()}
              className="rounded-xl border border-slate-200 bg-white text-slate-900 px-4 py-3 text-sm font-bold cursor-pointer hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              Clear all
            </button>
          </div>

          {/* Layout */}
          <div className="grid grid-cols-2 xs:grid-cols-1 sm:grid-cols-1 gap-4 items-start">

            {/* Main Panel */}
            <section className="bg-white rounded-3xl shadow-lg overflow-hidden">
              {/* Tabs */}
              <div className="flex flex-wrap gap-2 px-4 pt-4 pb-0 bg-white border-b border-slate-200">
                {appData.map((domain) => (
                  <button
                    key={domain.id}
                    type="button"
                    onClick={() => this.setState({ activeTab: domain.id, expandAll: false })}
                    className={`border px-3.5 py-2.5 rounded-full font-bold text-sm cursor-pointer mb-3 transition-colors ${
                      domain.id === activeTab && !expandAll
                        ? "bg-sky-700 border-sky-700 text-white"
                        : "bg-white border-slate-200 text-slate-500 hover:border-sky-300 hover:text-sky-700"
                    }`}
                  >
                    {domain.label}
                  </button>
                ))}
              </div>

              {/* Tab Contents */}
              <div className="p-4">
                {appData.map((domain) => {
                  const isVisible = expandAll || domain.id === activeTab;
                  if (!isVisible) return null;

                  const visibleItems = domain.items.filter((item) =>
                    this.matchesFilters(domain, item)
                  );

                  return (
                    <div key={domain.id} className={expandAll ? "mb-6" : ""}>
                      <h2 className="text-base font-bold text-slate-700 mb-1">{domain.label}</h2>
                      <p className="text-slate-500 text-sm mb-3">{domain.note}</p>
                      {visibleItems.length === 0 ? (
                        <p className="text-slate-400 italic text-sm py-1">
                          No matching items for the current filters in this tab.
                        </p>
                      ) : (
                        <div className="grid gap-3.5">
                          {visibleItems.map((item) => this.renderCard(item))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Summary Panel */}
            <aside className="bg-white rounded-3xl shadow-lg p-4 xl:sticky xl:top-3.5">
              {/* Print-only header */}
              <div className="hidden print:block mb-4">
                <h1 className="text-2xl font-bold mb-1">Care Plan Summary</h1>
                <p className="text-slate-500 text-sm">
                  Selected target symptoms, therapy options, medication options, referral to, brief explanations, and expected outcomes.
                </p>
              </div>

              <h2 className="text-3xl my-4 font-bold mb-1">Mental Health Plan</h2>
     

              <div className="flex gap-2.5 flex-wrap mb-3 print:hidden">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="rounded-xl border border-sky-700 bg-sky-700 text-white px-4 py-3 text-sm font-bold cursor-pointer hover:bg-sky-800 transition-colors"
                >
                  Print summary
                </button>
                <button
                  type="button"
                  onClick={() => this.copySummary()}
                  className="rounded-xl border border-slate-200 bg-white text-slate-900 px-4 py-3 text-sm font-bold cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  {copied ? "Copied!" : "Copy summary"}
                </button>
              </div>

              <div className="border border-slate-200 bg-[#fbfdff] rounded-2xl p-4 min-h-[280px] max-h-[calc(100vh-260px)] overflow-auto">
                {summaryList.length === 0 ? (
                  <p className="text-slate-400 italic text-sm">
                    Select a target symptom, at least one therapy/support option, medication, or referral option to generate the summary.
                  </p>
                ) : (
                  summaryList.map((entry, i) => (
                    <div
                      key={i}
                      className="border border-slate-200 rounded-2xl p-3.5 bg-white mb-3 last:mb-0"
                    >
                      <span className="inline-block mr-2 mb-2 bg-sky-50 text-sky-700 px-2.5 py-1 rounded-full text-xs font-bold">
                        {entry.domain}
                      </span>
                      <h3 className="text-2xl font-bold leading-tight mb-1">{entry.symptom}</h3>

                      {entry.therapies.length > 0 && (
                        <>
                          <div className="mt-3 mb-1.5 text-xs uppercase tracking-widest text-teal-700 font-bold">
                            Therapy / support options
                          </div>
                          <ul className="m-0 pl-4 text-slate-900 list-disc">
                            {entry.therapies.map((t, j) => (
                              <li key={j} className="mb-1.5 text-sm leading-snug">
                                <strong>{t.label}:</strong> {t.brief}{" "}
                                <br />
                                <strong>Expected outcome:</strong> {t.expected}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      {entry.medications.length > 0 && (
                        <>
                          <div className="mt-3 mb-1.5 text-xs uppercase tracking-widest text-violet-700 font-bold">
                            Medication options
                          </div>
                          <ul className="m-0 pl-4 text-slate-900 list-disc">
                            {entry.medications.map((m, j) => (
                              <li key={j} className="mb-1.5 text-sm leading-snug">
                                <strong>{m.label}:</strong> {m.brief}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      {entry.referrals.length > 0 && (
                        <>
                          <div className="mt-3 mb-1.5 text-xs uppercase tracking-widest text-teal-700 font-bold">
                            Referral to
                          </div>
                          <ul className="m-0 pl-4 text-slate-900 list-disc">
                            {entry.referrals.map((r, j) => (
                              <li key={j} className="mb-1.5 text-sm leading-snug">
                                <strong>{r.label}:</strong> {r.brief}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }
}

export default MentalHealthPlan;
